import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// Hook adaptado para usar formulario_plano_acao_id em vez de conformidade_id
export const calcularScoreGUT = (gravidade: number, urgencia: number, tendencia: number): number => {
  return gravidade * urgencia * tendencia;
};

export const getGUTPrioridade = (score: number): string => {
  if (score >= 64) return 'critica';
  if (score >= 27) return 'alta';
  if (score >= 8) return 'media';
  return 'baixa';
};

export const GUT_LABELS = {
  gravidade: {
    1: 'Sem gravidade',
    2: 'Pouco grave',
    3: 'Grave',
    4: 'Muito grave',
    5: 'Extremamente grave'
  },
  urgencia: {
    1: 'Pode esperar',
    2: 'Pouco urgente',
    3: 'Urgente',
    4: 'Muito urgente',
    5: 'Ação imediata'
  },
  tendencia: {
    1: 'Não piora',
    2: 'Piora a longo prazo',
    3: 'Piora a médio prazo',
    4: 'Piora a curto prazo',
    5: 'Piora rapidamente'
  }
};

interface AvaliacaoGUTFormulario {
  id: string;
  formulario_plano_acao_id: string;
  gravidade: number;
  urgencia: number;
  tendencia: number;
  gravidade_justificativa?: string | null;
  urgencia_justificativa?: string | null;
  tendencia_justificativa?: string | null;
  score_gut?: number | null;
  prioridade?: string | null;
  avaliado_por?: string | null;
  avaliado_em: string;
  avaliador?: { id: string; name: string } | null;
}

interface AvaliacaoGUTFormularioInsert {
  id?: string;
  formulario_plano_acao_id: string;
  gravidade: number;
  urgencia: number;
  tendencia: number;
  gravidade_justificativa?: string | null;
  urgencia_justificativa?: string | null;
  tendencia_justificativa?: string | null;
  score_gut?: number | null;
  prioridade?: string | null;
  avaliado_por?: string | null;
}

export function useAvaliacaoGUTFormulario(formularioPlanoAcaoId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['avaliacao-gut-formulario', formularioPlanoAcaoId],
    queryFn: async () => {
      if (!formularioPlanoAcaoId) return null;
      const { data, error } = await supabase
        .from('avaliacao_gut')
        .select(`
          *,
          avaliador:users!avaliacao_gut_avaliado_por_fkey(id, name)
        `)
        .eq('formulario_plano_acao_id', formularioPlanoAcaoId)
        .maybeSingle();
      if (error) throw error;
      return data as AvaliacaoGUTFormulario | null;
    },
    enabled: !!formularioPlanoAcaoId
  });

  const upsertMutation = useMutation({
    mutationFn: async (data: AvaliacaoGUTFormularioInsert) => {
      // score_gut e prioridade são colunas geradas pelo banco - não incluir no payload
      const payload: Record<string, unknown> = {
        gravidade: data.gravidade,
        urgencia: data.urgencia,
        tendencia: data.tendencia,
        gravidade_justificativa: data.gravidade_justificativa,
        urgencia_justificativa: data.urgencia_justificativa,
        tendencia_justificativa: data.tendencia_justificativa,
        avaliado_por: data.avaliado_por,
        formulario_plano_acao_id: data.formulario_plano_acao_id,
      };

      if (data.id) {
        payload.id = data.id;
      }

      // Usar raw query para permitir conformidade_id null
      const { data: result, error } = await supabase
        .from('avaliacao_gut')
        .upsert(payload as any, { onConflict: 'id' })
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['avaliacao-gut-formulario', formularioPlanoAcaoId] });
      queryClient.invalidateQueries({ queryKey: ['formulario-planos-acao'] });
      toast({ title: 'Avaliação GUT salva com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao salvar avaliação GUT', description: error.message, variant: 'destructive' });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('avaliacao_gut').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['avaliacao-gut-formulario', formularioPlanoAcaoId] });
      toast({ title: 'Avaliação GUT excluída' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao excluir avaliação', description: error.message, variant: 'destructive' });
    }
  });

  return {
    avaliacao: query.data,
    isLoading: query.isLoading,
    error: query.error,
    saveAvaliacao: upsertMutation.mutate,
    deleteAvaliacao: deleteMutation.mutate,
    isSaving: upsertMutation.isPending,
    isDeleting: deleteMutation.isPending
  };
}
